import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import styled, { createGlobalStyle } from "styled-components";
import Card from "../components/Card";
import ActivityFeed from "../components/ActivityFeed";
import SurveyFrequencyChart from "../components/SurveyFrequencyChart";
import PharmacySearchChart from "../components/PharmacySearchChart";
import { fetchDashboardStats } from "../services/api";
import { easternDateString } from "../utils/formatters";
import PharmacyOrderChart from "../components/PharmacyOrderChart";
import UserBreakdownChart from "../components/UserBreakdownChart";
import UserOrdersChart from "../components/UserBreakdownChart";
import MedicationVariantCharts from "../components/MedicationVarientChart";

// Define chart color variables directly in the component
const ChartColors = createGlobalStyle`
  :root {
    /* Light mode chart colors */
    --chart-primary: #4338ca;      /* Deep indigo for patients */
    --chart-secondary: #0284c7;    /* Deeper blue for surveys */
    --chart-tertiary: #ea580c;     /* Burnt orange for orders */
    --chart-quaternary: #65a30d;   /* Olive green for other metrics */
    --chart-matched: #10b981;      /* Green for matched patients */
    --chart-unmatched: #f97316;    /* Orange for unmatched patients */
    --chart-new-order: #eab308;    /* Yellow for new/to be posted orders */
    --chart-complete-order: #16a34a; /* Green for complete orders */
    --chart-refill-order: #f59e0b;  /* Amber for refill orders */
    --chart-primary-translucent: rgba(67, 56, 202, 0.3); /* Translucent primary for focus effects */
    
    /* New Light mode button colors */
    --button-primary-bg: #3b82f6;          /* Blue for primary buttons */
    --button-primary-hover: #2563eb;       /* Darker blue for hover */
    --button-primary-text: #ffffff;        /* White text for primary buttons */
    --button-secondary-bg: #f3f4f6;        /* Light gray for secondary buttons */
    --button-secondary-hover: #e5e7eb;     /* Slightly darker gray for hover */
    --button-secondary-text: #374151;      /* Dark gray text for secondary buttons */
    --button-selected-bg: #2563eb;         /* Darker blue for selected state */
    --button-selected-text: #ffffff;       /* White text for selected state */
    --button-selected-hover: #1d4ed8;      /* Even darker blue for selected hover */
    --filter-button-border: #d1d5db;       /* Light gray border for filter buttons */
    --apply-button-bg: #16a34a;            /* Green for apply button */
    --apply-button-hover: #15803d;         /* Darker green for apply button hover */
    --clear-button-bg: #f3f4f6;            /* Light gray for clear button */
    --clear-button-text: #4b5563;          /* Medium gray text for clear button */
    --clear-button-hover: #e5e7eb;         /* Slightly darker gray for clear button hover */
  }

  [data-theme="dark"] {
    /* Dark mode chart colors */
    --chart-primary: #818cf8;      /* Bright indigo for patients */
    --chart-secondary: #38bdf8;    /* Bright sky blue for surveys */
    --chart-tertiary: #fb923c;     /* Bright orange for orders */
    --chart-quaternary: #a3e635;   /* Bright lime for other metrics */
    --chart-matched: #34d399;      /* Bright green for matched patients */
    --chart-unmatched: #fb923c;    /* Bright orange for unmatched patients */
    --chart-new-order: #fcd34d;    /* Bright yellow for new/to be posted orders */
    --chart-complete-order: #4ade80; /* Bright green for complete orders */
    --chart-refill-order: #fbbf24;  /* Bright amber for refill orders */
    --chart-primary-translucent: rgba(129, 140, 248, 0.3); /* Translucent primary for focus effects */
    
    /* Dark mode button colors */
    --button-primary-bg: #4f46e5;          /* Indigo for primary buttons */
    --button-primary-hover: #4338ca;       /* Darker indigo for hover */
    --button-primary-text: #ffffff;        /* White text for primary buttons */
    --button-secondary-bg: #1f2937;        /* Dark blue-gray for secondary buttons */
    --button-secondary-hover: #374151;     /* Lighter blue-gray for hover */
    --button-secondary-text: #e5e7eb;      /* Light gray text for secondary buttons */
    --button-selected-bg: #6366f1;         /* Bright indigo for selected state */
    --button-selected-text: #ffffff;       /* White text for selected state */
    --button-selected-hover: #4f46e5;      /* Slightly darker indigo for selected hover */
    --filter-button-border: #4b5563;       /* Medium gray border for filter buttons */
    --apply-button-bg: #10b981;            /* Emerald green for apply button */
    --apply-button-hover: #059669;         /* Darker emerald for apply button hover */
    --clear-button-bg: #1f2937;            /* Dark blue-gray for clear button */
    --clear-button-text: #d1d5db;          /* Light gray text for clear button */
    --clear-button-hover: #374151;         /* Medium gray for clear button hover */
  }
`;

// Modified container to align content to the left
const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-right: 1.5rem;
  padding-left: 1.5rem;
  margin: 0; /* Remove any default margins */
  align-items: flex-start; /* Align all children to the left */

  @media (max-width: 1024px) {
    padding-right: 1rem;
    padding-left: 1rem;
  }
`;

const MainSection = styled.div`
  width: 100%; /* Ensure this takes full width */
`;

const FilterSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 1rem;
  background-color: var(--card-bg);
  border-radius: var(--border-radius);
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  width: 100%; /* Ensure this takes full width */
`;

const QuickActionsSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  width: 100%;

  [data-theme="dark"] & {
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  }
`;

const QuickActionLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  min-width: fit-content;
`;

const QuickActionSeparator = styled.div`
  width: 1px;
  height: 32px;
  background: linear-gradient(180deg, transparent 0%, rgba(148, 163, 184, 0.3) 20%, rgba(148, 163, 184, 0.6) 50%, rgba(148, 163, 184, 0.3) 80%, transparent 100%);
  margin: 0 0.5rem;
`;

const QuickActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${(props) =>
    props.selected
      ? "var(--primary-color)"
      : props.theme.darkMode
      ? "var(--button-secondary-dark-hover)"
      : "var(--button-secondary-hover)"};
  color: ${(props) =>
    props.selected
      ? "var(--button-selected-text)"
      : props.theme.darkMode
      ? "var(--button-selected-text)"
      : "var(--gray-700)"};
  text-shadow: ${(props) =>
    props.theme.darkMode && !props.selected
      ? "0px 0px 1px rgba(0, 0, 0, 0.5)"
      : "none"};
  font-weight: ${(props) =>
    props.theme.darkMode && !props.selected ? "600" : "500"};
  border-color: ${(props) =>
    props.selected ? "var(--primary-dark)" : "var(--filter-button-border)"};
  box-shadow: ${(props) =>
    props.selected ? "0 1px 3px rgba(0, 0, 0, 0.1)" : "none"};

  &:hover {
    background-color: ${(props) =>
      props.selected
        ? "var(--button-selected-hover)"
        : props.theme.darkMode
        ? "var(--button-secondary-dark-hover)"
        : "var(--button-secondary-hover)"};
    transform: ${(props) => (props.selected ? "translateY(-1px)" : "none")};
    box-shadow: ${(props) =>
      props.selected
        ? "0 2px 4px rgba(0, 0, 0, 0.15)"
        : "0 1px 2px rgba(0, 0, 0, 0.1)"};
  }

  &:active {
    transform: translateY(1px);
    box-shadow: none;
  }

  .icon {
    width: 14px;
    height: 14px;
    stroke: currentColor;
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const QuickActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

// Icon components
const CalendarIcon = () => (
  <svg className="icon" viewBox="0 0 24 24">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const BarChartIcon = () => (
  <svg className="icon" viewBox="0 0 24 24">
    <line x1="12" y1="20" x2="12" y2="10"></line>
    <line x1="18" y1="20" x2="18" y2="4"></line>
    <line x1="6" y1="20" x2="6" y2="16"></line>
  </svg>
);

const TrendingUpIcon = () => (
  <svg className="icon" viewBox="0 0 24 24">
    <polyline points="23,6 13.5,15.5 8.5,10.5 1,18"></polyline>
    <polyline points="17,6 23,6 23,12"></polyline>
  </svg>
);

const TrendingDownIcon = () => (
  <svg className="icon" viewBox="0 0 24 24">
    <polyline points="23,18 13.5,8.5 8.5,13.5 1,6"></polyline>
    <polyline points="17,18 23,18 23,12"></polyline>
  </svg>
);

const DollarSignIcon = () => (
  <svg className="icon" viewBox="0 0 24 24">
    <line x1="12" y1="1" x2="12" y2="23"></line>
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
  </svg>
);

const MinusIcon = () => (
  <svg className="icon" viewBox="0 0 24 24">
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

const PlusIcon = () => (
  <svg className="icon" viewBox="0 0 24 24">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

const TargetIcon = () => (
  <svg className="icon" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10"></circle>
    <circle cx="12" cy="12" r="6"></circle>
    <circle cx="12" cy="12" r="2"></circle>
  </svg>
);

const FilterTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: var(--text-primary);
  text-align: left; /* Ensure text is left-aligned */
`;

const FilterControls = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    justify-content: flex-end;
  }
`;

const DateInput = styled.input`
  padding: 0.5rem;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background-color: var(--input-bg);
  color: var(--text-primary);
  font-size: 0.875rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    background-color: var(--card-bg);
  }

  &:hover {
    border-color: var(--primary-color);
  }

  /* Style the calendar icon in webkit browsers */
  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    filter: var(--icon-filter, invert(0.5));
  }

  [data-theme="dark"] &::-webkit-calendar-picker-indicator {
    filter: invert(0.8);
  }
`;

const PriceInput = styled.input`
  padding: 0.5rem;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background-color: var(--input-bg);
  color: var(--text-primary);
  font-size: 0.875rem;
  width: 100px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    background-color: var(--card-bg);
  }

  &:hover {
    border-color: var(--primary-color);
  }

  /* Remove spinner arrows in Chrome, Safari, Edge */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Remove spinner arrows in Firefox */
  &[type=number] {
    -moz-appearance: textfield;
  }

  /* Prevent non-numeric input */
  &::placeholder {
    color: var(--text-secondary);
    opacity: 0.7;
  }
`;

const FilterButton = styled.button`
  background-color: #96c288;
  color: var(--button-primary-text);
  border: none;
  border-radius: 50px;
  padding: 0.5rem 1.25rem;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover {
    background-color: var(--apply-button-hover);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(1px);
    box-shadow: none;
  }
`;

const ClearFilterButton = styled.button`
  background-color: var(--clear-button-bg);
  color: var(--clear-button-text);
  border: 1px solid var(--filter-button-border);
  border-radius: 50px;
  padding: 0.5rem 1.25rem;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: var(--clear-button-hover);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  &:active {
    transform: translateY(1px);
    box-shadow: none;
  }
`;

const QuickFilterButton = styled.button`
  background-color: ${(props) =>
    props.selected
      ? "var(--primary-color)"
      : props.theme.darkMode
      ? "var(--button-secondary-dark-hover)"
      : "var(--button-secondary-hover)"};
  color: ${(props) =>
    props.selected
      ? "var(--button-selected-text)"
      : props.theme.darkMode
      ? "var(--button-selected-text)"
      : "var(--gray-700)"};
  text-shadow: ${(props) =>
    props.theme.darkMode && !props.selected
      ? "0px 0px 1px rgba(0, 0, 0, 0.5)"
      : "none"};
  font-weight: ${(props) =>
    props.theme.darkMode && !props.selected ? "600" : "500"};
  border: 1px solid
    ${(props) =>
      props.selected ? "var(--primary-dark)" : "var(--filter-button-border)"};
  border-radius: 50px;
  padding: 0.5rem 1rem;
  transform: translateY(1px);
  box-shadow: none;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${(props) =>
    props.selected ? "0 1px 3px rgba(0, 0, 0, 0.1)" : "none"};

  &:hover {
    background-color: ${(props) =>
      props.selected
        ? "var(--button-selected-hover)"
        : props.theme.darkMode
        ? "var(--button-secondary-dark-hover)"
        : "var(--button-secondary-hover)"};
    transform: ${(props) => (props.selected ? "translateY(-1px)" : "none")};
    box-shadow: ${(props) =>
      props.selected
        ? "0 2px 4px rgba(0, 0, 0, 0.15)"
        : "0 1px 2px rgba(0, 0, 0, 0.1)"};
  }

  &:active {
    transform: translateY(1px);
    box-shadow: none;
  }
`;

// New styled component for price filter button
const PriceFilterButton = styled(QuickFilterButton)`
 background-color: ${(props) =>
   props.selected ? "#1d4ed8" : "var(--button-secondary-bg)"};
 border-color: ${(props) =>
   props.selected ? "#3b82f6" : "var(--filter-button-border)"};
 color: ${(props) =>
   props.selected ? "#ffffff" : "var(--button-primary-text)"};
 font-weight: ${(props) => props.selected ? "600" : "500"};
 
 &:hover {
   background-color: ${(props) =>
     props.selected ? "#1e3a8a" : "var(--button-secondary-hover)"};
   border-color: ${(props) =>
     props.selected ? "#1e3a8a" : "var(--filter-button-border)"};
   color: ${(props) =>
     props.selected ? "#ffffff" : "var(--button-primary-text)"};
 }

 &:active {
   background-color: ${(props) =>
     props.selected ? "#1e3a8a" : "var(--button-secondary-hover)"};
   color: ${(props) =>
     props.selected ? "#ffffff" : "var(--button-secondary-text)"};
 }
`;

const PriceFilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border: 1px solid var(--filter-button-border);
  border-radius: 8px;
  background-color: var(--button-secondary-bg);

  label {
    font-size: 0.75rem;
    color: var(--text-secondary);
    margin: 0;
  }
`;

// Modified charts grid to align left
const TopChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;
  width: 100%; /* Ensure full width */
  justify-content: start; /* Align grid items to the left */

  @media (min-width: 1500px) {
    grid-template-columns: 1fr 1fr;
  }
`;

// Modified bottom charts grid to align left
const BottomChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;
  width: 100%; /* Ensure full width */
  justify-content: start; /* Align grid items to the left */

  @media (min-width: 1500px) {
    grid-template-columns: 1fr 1fr;
  }
`;

// Modified chart section component with left alignment
const ChartSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  width: 100%; /* Ensure full width */
  align-items: flex-start; /* Explicitly align content to the left */
`;

// Modified chart title to ensure left alignment
const ChartTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  color: var(--text-primary);
  padding-left: 0;
  text-align: left; /* Ensure text alignment is left */
  align-self: flex-start; /* Align to the left of the flex container */
`;

// const ChartCard = styled.div`
//   height: 450px;
//   background-color: var(--card-bg);
//   border-radius: var(--border-radius);
//   border: 1px solid var(--border-color);
//   overflow: hidden;
//   width: 100%; /* Ensure full width */

//   .chart-placeholder {
//     flex: 1;
//     display: flex;
//     align-items: center;
//     justify-content: flex-start; /* Left align content */
//     color: var(--gray-400);
//     font-size: 0.875rem;
//     background: var(--card-highlight-bg);
//     border-radius: var(--border-radius);
//     height: 100%;
//     overflow: auto;
//     padding-left: 1rem; /* Add some padding for better alignment */
//   }
// `;

const ChartCard = styled.div`
  height: auto;
  min-height: 450px;

  &.medication-charts {
    min-height: 800px;

    @media (min-width: 1200px) {
      min-height: 500px;
    }
  }
`;

// Modified horizontal stats card for left alignment
const HorizontalStatsCard = styled(Card)`
  border-top: 4px solid ${(props) => props.color || "var(--border-color)"};
  padding: 1.5rem;
  position: relative;
  margin-bottom: 2rem;
  width: 100%; /* Ensure full width */
  text-align: left; /* Left align text content */

  .stats-title {
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 1rem;
    color: var(--text-primary);
    text-align: left; /* Ensure text alignment is left */
  }

  .stats-row {
    display: flex;
    width: 100%;
    gap: 1rem;
    justify-content: flex-start; /* Align items to the left */

    @media (max-width: 1024px) {
      flex-wrap: wrap;
    }
  }

  .stat-item {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 1rem;
    background-color: var(--card-highlight-bg);
    border-radius: var(--border-radius);
    border: 1px solid var(--border-color);
    text-align: left; /* Ensure text alignment is left */

    @media (max-width: 1024px) {
      flex: 1 1 calc(33% - 1rem);
    }

    @media (max-width: 768px) {
      flex: 1 1 calc(50% - 1rem);
    }

    @media (max-width: 480px) {
      flex: 1 1 100%;
    }
  }

  .stat-main {
    flex: 1.5;

    @media (max-width: 1024px) {
      flex: 1 1 100%;
    }
  }

  .stat-title {
    font-size: 0.75rem;
    color: var(--text-secondary);
    margin-bottom: 0.35rem;
    text-align: left; /* Ensure text alignment is left */
  }

  .stat-value {
    font-size: 1.75rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 0.25rem;
    text-align: left; /* Ensure text alignment is left */
  }

  .stat-change {
    font-size: 0.75rem;
    display: flex;
    align-items: center;
    color: var(--text-secondary);
    justify-content: flex-start; /* Align to the left */

    .positive {
      color: var(--success);
    }

    .negative {
      color: var(--error);
    }
  }

  .completion-bar {
    margin-top: 0.5rem;
  }

  .progress-label {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.25rem;

    .label {
      font-size: 0.75rem;
      color: var(--text-secondary);
      text-align: left; /* Ensure text alignment is left */
    }

    .value {
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--text-primary);
    }
  }

  .progress-track {
    height: 4px;
    background-color: var(--border-color);
    border-radius: 2px;
    overflow: hidden;

    .progress-fill {
      height: 100%;
      background-color: #96c288;
      border-radius: 2px;
      transition: width 0.3s ease;
    }
  }

  .loading-placeholder {
    height: 16px;
    background-color: var(--card-highlight-bg);
    border-radius: 4px;
    animation: pulse 1.5s infinite;
    margin-bottom: 0.5rem;

    &.title {
      width: 60%;
    }

    &.value {
      width: 40%;
      height: 24px;
    }

    &.change {
      width: 30%;
    }
  }

  @keyframes pulse {
    0% {
      opacity: 0.6;
    }
    50% {
      opacity: 0.8;
    }
    100% {
      opacity: 0.6;
    }
  }
`;

// Unified Order Stats Component in a horizontal layout
const HorizontalOrderStatsCard = ({ orderStats, isLoading }) => {
  // Helper function to render change indicators
  const renderChange = (changeValue) => {
    if (changeValue === undefined || changeValue === null) return null;
    if (changeValue === 0) return <span>0%</span>;
    return changeValue > 0 ? (
      <span className="positive">↑ {changeValue}%</span>
    ) : (
      <span className="negative">↓ {Math.abs(changeValue)}%</span>
    );
  };

  // Calculate completion percentage
  const completionPercentage =
    orderStats.total > 0
      ? Math.round((orderStats.completed / orderStats.total) * 100)
      : 0;

  if (isLoading) {
    return (
      <HorizontalStatsCard color="var(--chart-tertiary)">
        <div className="stats-title">Order Statistics</div>
        <div className="stats-row">
          <div className="stat-item stat-main">
            <div className="loading-placeholder title"></div>
            <div className="loading-placeholder value"></div>
            <div className="loading-placeholder change"></div>
          </div>
          <div className="stat-item">
            <div className="loading-placeholder title"></div>
            <div className="loading-placeholder value"></div>
            <div className="loading-placeholder change"></div>
          </div>
          <div className="stat-item">
            <div className="loading-placeholder title"></div>
            <div className="loading-placeholder value"></div>
            <div className="loading-placeholder change"></div>
          </div>
          <div className="stat-item">
            <div className="loading-placeholder title"></div>
            <div className="loading-placeholder value"></div>
            <div className="loading-placeholder change"></div>
          </div>
          <div className="stat-item">
            <div className="loading-placeholder title"></div>
            <div className="loading-placeholder value"></div>
            <div className="loading-placeholder change"></div>
          </div>
        </div>
      </HorizontalStatsCard>
    );
  }

  return (
    <HorizontalStatsCard color="var(--chart-tertiary)">
      <div className="stats-title">Order Statistics</div>
      <div className="stats-row">
        {/* Total Orders */}
        <div className="stat-item stat-main">
          <div className="stat-title">Total Orders</div>
          <div className="stat-value">{orderStats.total}</div>
          {/* <div className="stat-change">
            {renderChange(orderStats.totalChange)}{" "}
            <span style={{ marginLeft: "4px" }}>this period</span>
          </div> */}
          <div className="completion-bar">
            <div className="progress-label">
              <div className="label">Completion Rate</div>
              <div className="value">{completionPercentage}%</div>
            </div>
            <div className="progress-track">
              <div
                className="progress-fill"
                style={{ width: `${completionPercentage}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* New Orders */}
        <div className="stat-item">
          <div className="stat-title">New Orders</div>
          <div className="stat-value">{orderStats.newOrders}</div>
          {/* <div className="stat-change">{renderChange(orderStats.newOrdersChange)}</div> */}
        </div>

        {/* Refill Orders */}
        <div className="stat-item">
          <div className="stat-title">Refill Orders</div>
          <div className="stat-value">{orderStats.refillOrders}</div>
          {/* <div className="stat-change">{renderChange(orderStats.refillOrdersChange)}</div> */}
        </div>

        {/* To Be Posted */}
        <div className="stat-item">
          <div className="stat-title">To Be Posted</div>
          <div className="stat-value">{orderStats.toBePosted}</div>
          {/* <div className="stat-change">{renderChange(orderStats.toBePostedChange)}</div> */}
        </div>

        {/* Completed */}
        <div className="stat-item">
          <div className="stat-title">Completed</div>
          <div className="stat-value">{orderStats.completed}</div>
          {/* <div className="stat-change">{renderChange(orderStats.completedChange)}</div> */}
        </div>
      </div>
    </HorizontalStatsCard>
  );
};

function DashboardPage() {
  const [stats, setStats] = useState({
    // medication_breakdown: null,
    user_breakdown: null,
    order_type_breakdown: null,
    pharmacy_breakdown: null,
    state_breakdown: null,
    total_statistics: null,
    variant_breakdown: null,
    medication_breakdown: null,

    patients: 0,
    orders: 0,
    surveys: 0,
    matched: 0,
    unmatched: 0,
    patientChange: 0,
    orderChange: 0,
    surveyChange: 0,
    sideEffectRate: 0,
    averageEffectiveness: 0,
    chartData: [],
    orderStats: {
      total: 2,
      newOrders: 2,
      refillOrders: 0,
      toBePosted: 0,
      completed: 2,
      totalChange: 3,
      newOrdersChange: 5,
      refillOrdersChange: -2,
      toBePostedChange: 3,
      completedChange: 8
    },
    pharmacyOrderData: [
      { name: "PharmaCare Plus", orders: 78 },
      { name: "MediPharm", orders: 65 },
      { name: "HealthRx", orders: 52 },
      { name: "Wellness Pharmacy", orders: 48 },
      { name: "CareFirst Rx", orders: 42 },
      { name: "NationalMed", orders: 38 },
      { name: "QuickCare Pharmacy", orders: 32 }
    ],
    surveyTrendData: [
      { date: "2025-05-01", count: 12 },
      { date: "2025-05-02", count: 18 },
      { date: "2025-05-03", count: 15 },
      { date: "2025-05-04", count: 8 },
      { date: "2025-05-05", count: 21 },
      { date: "2025-05-06", count: 24 },
      { date: "2025-05-07", count: 19 },
      { date: "2025-05-08", count: 22 },
      { date: "2025-05-09", count: 27 },
      { date: "2025-05-10", count: 20 },
      { date: "2025-05-11", count: 14 },
      { date: "2025-05-12", count: 23 },
      { date: "2025-05-13", count: 30 },
      { date: "2025-05-14", count: 26 }
    ]
  });

  const [isLoading, setIsLoading] = useState(true);

  const [user, setUser] = useState(null);

  // Updated filter state to include min/max price ranges
  const [filters, setFilters] = useState({
    startDate: easternDateString(30),
    endDate: easternDateString(),
    selectedRange: "30d",
    minPrice: "",
    maxPrice: ""
  });

  useEffect(() => {
    const userString = localStorage.getItem("user");
    if (userString) {
      const userObject = JSON.parse(userString);
      setUser(userObject);
    }

    // Manually trigger initial data load
    // setShouldRefresh(true);
  }, []);

  // Handle date filter changes
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      selectedRange: "custom"
    }));
  };

  // Handle price input changes with strict validation
  const handlePriceChange = (e) => {
    const { name, value } = e.target;
    
    // Only allow empty string or valid numeric input (including decimal points)
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setFilters((prev) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Additional handler to prevent non-numeric characters from being typed
  const handlePriceKeyPress = (e) => {
    // Allow: backspace, delete, tab, escape, enter, decimal point
    if ([8, 9, 27, 13, 46, 110, 190].indexOf(e.keyCode) !== -1 ||
        // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.keyCode === 65 && e.ctrlKey === true) ||
        (e.keyCode === 67 && e.ctrlKey === true) ||
        (e.keyCode === 86 && e.ctrlKey === true) ||
        (e.keyCode === 88 && e.ctrlKey === true)) {
      return;
    }
    // Ensure that it is a number and stop the keypress
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
  };

  // Handle paste events to prevent non-numeric content
  const handlePricePaste = (e) => {
    const paste = (e.clipboardData || window.clipboardData).getData('text');
    if (!/^\d*\.?\d*$/.test(paste)) {
      e.preventDefault();
    }
  };

  // Handle quick filter selection
  const handleQuickFilter = (range) => {
    const daysBack = { "7d": 7, "30d": 30, "90d": 90 }[range] ?? 30;

    const newFilters = {
      ...filters,
      startDate: easternDateString(daysBack),
      endDate: easternDateString(),
      selectedRange: range
    };

    setFilters(newFilters);

    // Immediately apply the filter when a quick filter button is clicked
    fetchDashboardData(
      newFilters.startDate,
      newFilters.endDate,
      newFilters.minPrice || null,
      newFilters.maxPrice || null
    );
  };

  // Handle preset price range filters
  const handlePresetPriceFilter = (minPrice, maxPrice, label) => {
    const newFilters = {
      ...filters,
      minPrice: minPrice?.toString() || "",
      maxPrice: maxPrice?.toString() || ""
    };

    setFilters(newFilters);

    // Immediately apply the filter
    fetchDashboardData(
      newFilters.startDate,
      newFilters.endDate,
      minPrice,
      maxPrice
    );
  };

  // Clear filter and set to last 7 days
  const handleClearFilter = () => {
    // Set to last 7 days and clear price filters
    const clearedFilters = {
      startDate: easternDateString(7),
      endDate: easternDateString(),
      selectedRange: "7d",
      minPrice: "",
      maxPrice: ""
    };

    setFilters(clearedFilters);

    // Fetch data with the 7-day range and no price filter
    fetchDashboardData(
      clearedFilters.startDate,
      clearedFilters.endDate,
      null,
      null
    );
  };

  // Apply filter and fetch data
  const applyFilter = () => {
    fetchDashboardData(
      filters.startDate, 
      filters.endDate, 
      filters.minPrice || null,
      filters.maxPrice || null
    );
  };

  // Updated fetchDashboardData function to include min/max price parameters
  const fetchDashboardData = async (startDate, endDate, minPrice = null, maxPrice = null) => {
    setIsLoading(true);
    try {
      const dashboardData = await fetchDashboardStats(startDate, endDate, minPrice, maxPrice);

      // Process dashboard data and update state
      const newOrdersCount =
        dashboardData.order_type_breakdown?.new_orders || 0;
      const refillOrdersCount =
        dashboardData.order_type_breakdown?.refill_orders || 0;
      const toBePostedCount = dashboardData.total_statistics?.to_be_posted || 0;
      const completedCount = dashboardData.total_statistics?.completed || 0;
      const totalOrders = newOrdersCount + refillOrdersCount;

      setStats({
        user_breakdown: dashboardData.user_breakdown,
        medication_breakdown: dashboardData.medication_breakdown,
        order_type_breakdown: dashboardData.order_type_breakdown,
        pharmacy_breakdown: dashboardData.pharmacy_breakdown,
        state_breakdown: dashboardData.state_breakdown,
        total_statistics: dashboardData.total_statistics,
        variant_breakdown: dashboardData.variant_breakdown,
        medication_breakdown: dashboardData.medication_breakdown,
        survey_frequency: dashboardData.survey_frequency,

        matched: dashboardData.matched_patients || 0,
        unmatched: dashboardData.unmatched_patients || 0,
        patients: dashboardData.total_patients || 0,
        orders: totalOrders,
        surveys: dashboardData.total_surveys || 0,
        patientChange: dashboardData.patient_change || 0,
        orderChange: dashboardData.order_change || 0,
        surveyChange: dashboardData.survey_change || 0,
        sideEffectRate: dashboardData.side_effect_rate || 0,
        averageEffectiveness: dashboardData.average_effectiveness || 0,
        chartData: dashboardData.chart_data || [],

        // Updated unified order stats format
        orderStats: {
          total: totalOrders,
          newOrders: newOrdersCount,
          refillOrders: refillOrdersCount,
          toBePosted: toBePostedCount,
          completed: completedCount,
          totalChange: dashboardData.order_change || 0,
          newOrdersChange: 5, // Placeholder - update with real data when available
          refillOrdersChange: -2, // Placeholder - update with real data when available
          toBePostedChange: 3, // Placeholder - update with real data when available
          completedChange: 8 // Placeholder - update with real data when available
        },

        pharmacyOrderData: dashboardData.pharmacy_breakdown,
        surveyTrendData: dashboardData.survey_frequency
      });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // The stats are superuser-only; read the user synchronously so non-admin
  // staff skip the request and go straight to Orders instead of a blank page
  const isSuperuser = (() => {
    try {
      return Boolean(JSON.parse(localStorage.getItem("user") || "null")?.is_superuser);
    } catch {
      return false;
    }
  })();

  useEffect(() => {
    if (isSuperuser) fetchDashboardData(filters.startDate, filters.endDate);
  }, []);

  if (!isSuperuser) {
    return <Navigate to="/orders" replace />;
  }

  if (user?.is_superuser) {
    return (
      <>
        <ChartColors />

        <DashboardContainer>
          {/* Advanced Filter Section */}
          <FilterSection>
            <FilterTitle>Advanced Filters</FilterTitle>
            <FilterControls>
              {/* Custom Price Range Inputs */}
              <PriceFilterGroup>
                <label>Min:</label>
                <PriceInput
                  type="number"
                  name="minPrice"
                  value={filters.minPrice}
                  onChange={handlePriceChange}
                  onKeyDown={handlePriceKeyPress}
                  onPaste={handlePricePaste}
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
                <label>Max:</label>
                <PriceInput
                  type="number"
                  name="maxPrice"
                  value={filters.maxPrice}
                  onChange={handlePriceChange}
                  onKeyDown={handlePriceKeyPress}
                  onPaste={handlePricePaste}
                  placeholder="∞"
                  min="0"
                  step="0.01"
                />
              </PriceFilterGroup>

              {/* Date Inputs */}
              <DateInput
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleDateChange}
              />
              <DateInput
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleDateChange}
              />
              
              {/* Apply and Clear Buttons */}
              <FilterButton onClick={applyFilter}>Apply</FilterButton>
              <ClearFilterButton onClick={handleClearFilter}>
                Clear
              </ClearFilterButton>
            </FilterControls>
          </FilterSection>

          {/* Quick Actions Section */}
          <QuickActionsSection>
            <QuickActionGroup>
              <QuickActionLabel>TIME</QuickActionLabel>
              <QuickActionButton
                selected={filters.selectedRange === "7d"}
                onClick={() => handleQuickFilter("7d")}
              >
                <CalendarIcon />
                Last 7 Days
              </QuickActionButton>
              <QuickActionButton
                selected={filters.selectedRange === "30d"}
                onClick={() => handleQuickFilter("30d")}
              >
                <CalendarIcon />
                Last 30 Days
              </QuickActionButton>
              <QuickActionButton
                selected={filters.selectedRange === "90d"}
                onClick={() => handleQuickFilter("90d")}
              >
                <BarChartIcon />
                Last 90 Days
              </QuickActionButton>
            </QuickActionGroup>

            <QuickActionSeparator />

            <QuickActionGroup>
              <QuickActionLabel>PRICE</QuickActionLabel>
              <QuickActionButton
                selected={filters.minPrice === "200" && filters.maxPrice === ""}
                onClick={() => handlePresetPriceFilter(200, null, "Above $200")}
              >
                <TrendingUpIcon />
                Above $200
              </QuickActionButton>
              <QuickActionButton
                selected={filters.minPrice === "" && filters.maxPrice === "100"}
                onClick={() => handlePresetPriceFilter(null, 100, "Below $100")}
              >
                <TrendingDownIcon />
                Below $100
              </QuickActionButton>
              <QuickActionButton
                selected={filters.minPrice === "50" && filters.maxPrice === "200"}
                onClick={() => handlePresetPriceFilter(50, 200, "$50-$200")}
              >
                <DollarSignIcon />
                $50-$200
              </QuickActionButton>
              <QuickActionButton
                selected={filters.minPrice === "1000" && filters.maxPrice === ""}
                onClick={() => handlePresetPriceFilter(1000, null, "Above $1000")}
              >
                <PlusIcon />
                Above $1000
              </QuickActionButton>
            </QuickActionGroup>
          </QuickActionsSection>

          <MainSection>
            {/* Horizontal Order Stats Card */}
            <HorizontalOrderStatsCard
              orderStats={stats.orderStats}
              isLoading={isLoading}
            />

            {/* Top Charts with titles outside of cards */}
            <TopChartsGrid>
              {/* Survey Frequency Chart with title outside */}
              <ChartSection>
                <ChartTitle>Surveys Report</ChartTitle>
                <ChartCard>
                  <SurveyFrequencyChart
                    surveyData={stats.surveyTrendData}
                    isLoading={isLoading}
                  />
                </ChartCard>
              </ChartSection>

              {/* Pharmacy Orders Bar Chart with title outside */}
              <ChartSection>
                <ChartTitle>Pharmacies Report</ChartTitle>
                <ChartCard>
                  <PharmacyOrderChart
                    pharmacyData={stats.pharmacyOrderData}
                    isLoading={isLoading}
                  />
                </ChartCard>
              </ChartSection>
            </TopChartsGrid>

            {/* Bottom Charts with different responsive behavior */}
            <BottomChartsGrid>
              {/* Pharmacy Orders with Search Filter */}
              <ChartSection>
                <ChartTitle>Order Report by State</ChartTitle>
                <ChartCard>
                  <PharmacySearchChart pharmacyData={stats.state_breakdown} />
                </ChartCard>
              </ChartSection>

              {/* Users Report */}
              <ChartSection>
                <ChartTitle>Users Report</ChartTitle>
                <ChartCard>
                  <UserOrdersChart userData={stats?.user_breakdown} />
                </ChartCard>
              </ChartSection>
            </BottomChartsGrid>

            {/* Full width Medication Report Section - moved outside the grid */}
            <ChartSection className="full-width">
              <ChartTitle>Medication Report</ChartTitle>
              <ChartCard className="medication-charts">
                <MedicationVariantCharts
                  medicationData={stats?.medication_breakdown}
                  variantData={stats?.variant_breakdown}
                  isLoading={isLoading}
                />
              </ChartCard>
            </ChartSection>
          </MainSection>
        </DashboardContainer>
      </>
    );
  }
}

export default DashboardPage;
